import React from 'react';
import Icon from '../../../components/AppIcon';
import { useTranslation } from 'react-i18next';

const FilterChips = ({ filters, onRemoveFilter, onClearAll }) => {
  const { t } = useTranslation();

  const getActiveFilters = () => {
    const activeFilters = [];

    // Categories
    if (filters.category?.length > 0) {
      filters.category.forEach(categoryId => {
        const categoryLabels = {
          'electronics': t('electronics'),
          'clothing': t('clothing'),
          'home': t('home'),
          'beauty': t('beauty'),
          'sports': t('sports'),
          'books': t('books'),
          'toys': t('toys'),
          'automotive': t('automotive')
        };
        activeFilters.push({
          type: 'category',
          value: categoryId,
          label: categoryLabels[categoryId] || categoryId,
          displayLabel: categoryLabels[categoryId] || categoryId
        });
      });
    }

    // Brands
    if (filters.brand?.length > 0) {
      filters.brand.forEach(brandId => {
        const brandLabels = {
          'apple': t('apple'),
          'samsung': t('samsung'),
          'nike': t('nike'),
          'adidas': t('adidas'),
          'sony': t('sony'),
          'lg': t('lg'),
          'hp': t('hp'),
          'dell': t('dell')
        };
        activeFilters.push({
          type: 'brand',
          value: brandId,
          label: brandLabels[brandId] || brandId,
          displayLabel: brandLabels[brandId] || brandId
        });
      });
    }

    // Price Range
    if (filters.priceRange) {
      activeFilters.push({
        type: 'priceRange',
        value: filters.priceRange.id,
        label: filters.priceRange.label,
        displayLabel: filters.priceRange.label
      });
    }

    // Custom Price
    if (filters.customPrice && (filters.customPrice.price_start || filters.customPrice.price_end)) {
      const min = filters.customPrice.price_start || '0';
      const max = filters.customPrice.price_end || '∞';
      activeFilters.push({
        type: 'customPrice',
        value: 'custom',
        label: `$${min} - $${max}`,
        displayLabel: `$${min} - $${max}`
      });
    }

    // Rating
    if (filters.rating) {
      activeFilters.push({
        type: 'rating',
        value: filters.rating,
        label: `${filters.rating}+ ${t('stars')}`,
        displayLabel: `${filters.rating}+ ${t('stars')}`
      });
    }

    // Features
    if (filters.features?.length > 0) {
      filters.features.forEach(featureId => {
        const featureLabels = {
          'free-shipping': t('freeShipping'),
          'on-sale': t('onSale'),
          'new-arrivals': t('newArrivals'),
          'best-seller': t('bestSeller'),
          'eco-friendly': t('ecoFriendly'),
          'fast-delivery': t('fastDelivery')
        };
        activeFilters.push({
          type: 'features',
          value: featureId,
          label: featureLabels[featureId] || featureId,
          displayLabel: featureLabels[featureId] || featureId
        });
      });
    }

    return activeFilters;
  };

  const activeFilters = getActiveFilters();

  if (activeFilters.length === 0) {
    return null;
  }

  const handleRemoveFilter = (filterType, filterValue) => {
    onRemoveFilter(filterType, filterValue);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 p-4 bg-muted/30 border-b border-border">
      <div className="flex items-center space-x-2 text-sm text-text-secondary">
        <Icon name="Filter" size={16} />
        <span>{t('activeFilters')}:</span>
      </div>

      <div className="flex flex-wrap gap-2">
        {activeFilters.map((filter, index) => (
          <div
            key={`${filter.type}-${filter.value}-${index}`}
            className="flex items-center space-x-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm border border-primary/20"
          >
            <span>{filter.displayLabel}</span>
            <button
              onClick={() => handleRemoveFilter(filter.type, filter.value)}
              className="hover:bg-primary/20 rounded-full p-0.5 transition-smooth"
              aria-label={t(`removeFilter`, { filter: filter.displayLabel })}
            >
              <Icon name="X" size={12} />
            </button>
          </div>
        ))}
      </div>

      {activeFilters.length > 1 && (
        <button
          onClick={onClearAll}
          className="flex items-center space-x-1 text-sm text-text-secondary hover:text-error transition-smooth ml-2"
        >
          <Icon name="RotateCcw" size={14} />
          <span>{t('clearAll')}</span>
        </button>
      )}
    </div>
  );
};

export default FilterChips;
